import { IUser, IWorkspace } from '../../types';

// User Action Types
export enum UserActionTypes {
  // Initialization actions
  INITIALIZE_AUTH = 'INITIALIZE_AUTH',

  // Authentication actions
  LOGIN_REQUEST = 'LOGIN_REQUEST',
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILURE = 'LOGIN_FAILURE',

  // Logout actions
  LOGOUT_REQUEST = 'LOGOUT_REQUEST',
  LOGOUT_SUCCESS = 'LOGOUT_SUCCESS',
  LOGOUT_FAILURE = 'LOGOUT_FAILURE',

  // User registration actions
  REGISTER_REQUEST = 'REGISTER_REQUEST',
  REGISTER_SUCCESS = 'REGISTER_SUCCESS',
  REGISTER_FAILURE = 'REGISTER_FAILURE',

  // User profile actions
  GET_USER_PROFILE_REQUEST = 'GET_USER_PROFILE_REQUEST',
  GET_USER_PROFILE_SUCCESS = 'GET_USER_PROFILE_SUCCESS',
  GET_USER_PROFILE_FAILURE = 'GET_USER_PROFILE_FAILURE',

  // Update user profile actions
  UPDATE_USER_PROFILE_REQUEST = 'UPDATE_USER_PROFILE_REQUEST',
  UPDATE_USER_PROFILE_SUCCESS = 'UPDATE_USER_PROFILE_SUCCESS',
  UPDATE_USER_PROFILE_FAILURE = 'UPDATE_USER_PROFILE_FAILURE',

  // Get user data actions
  GET_USER_DATA_REQUEST = 'GET_USER_DATA_REQUEST',
  GET_USER_DATA_SUCCESS = 'GET_USER_DATA_SUCCESS',
  GET_USER_DATA_FAILURE = 'GET_USER_DATA_FAILURE',

  // Get workspace data actions
  GET_WORKSPACE_DATA_REQUEST = 'GET_WORKSPACE_DATA_REQUEST',
  GET_WORKSPACE_DATA_SUCCESS = 'GET_WORKSPACE_DATA_SUCCESS',
  GET_WORKSPACE_DATA_FAILURE = 'GET_WORKSPACE_DATA_FAILURE',

  // Clear user actions
  CLEAR_USER_ERROR = 'CLEAR_USER_ERROR',
  CLEAR_USER_SUCCESS = 'CLEAR_USER_SUCCESS',

  // Set user data
  SET_USER_DATA = 'SET_USER_DATA',
  CLEAR_USER_DATA = 'CLEAR_USER_DATA',
}

// User Action Interfaces
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface InitializeAuthAction {
  type: UserActionTypes.INITIALIZE_AUTH;
}

export interface LoginRequestAction {
  type: UserActionTypes.LOGIN_REQUEST;
  payload: LoginCredentials;
}

export interface LoginSuccessAction {
  type: UserActionTypes.LOGIN_SUCCESS;
  payload: IUser;
}

export interface LoginFailureAction {
  type: UserActionTypes.LOGIN_FAILURE;
  payload: string;
}

export interface LogoutRequestAction {
  type: UserActionTypes.LOGOUT_REQUEST;
}

export interface LogoutSuccessAction {
  type: UserActionTypes.LOGOUT_SUCCESS;
}

export interface LogoutFailureAction {
  type: UserActionTypes.LOGOUT_FAILURE;
  payload: string;
}

export interface RegisterRequestAction {
  type: UserActionTypes.REGISTER_REQUEST;
  payload: Partial<IUser>;
}

export interface RegisterSuccessAction {
  type: UserActionTypes.REGISTER_SUCCESS;
  payload: IUser;
}

export interface RegisterFailureAction {
  type: UserActionTypes.REGISTER_FAILURE;
  payload: string;
}

export interface GetUserProfileRequestAction {
  type: UserActionTypes.GET_USER_PROFILE_REQUEST;
  payload: string;
}

export interface GetUserProfileSuccessAction {
  type: UserActionTypes.GET_USER_PROFILE_SUCCESS;
  payload: IUser;
}

export interface GetUserProfileFailureAction {
  type: UserActionTypes.GET_USER_PROFILE_FAILURE;
  payload: string;
}

export interface UpdateUserProfileRequestAction {
  type: UserActionTypes.UPDATE_USER_PROFILE_REQUEST;
  payload: Partial<IUser>;
}

export interface UpdateUserProfileSuccessAction {
  type: UserActionTypes.UPDATE_USER_PROFILE_SUCCESS;
  payload: IUser;
}

export interface UpdateUserProfileFailureAction {
  type: UserActionTypes.UPDATE_USER_PROFILE_FAILURE;
  payload: string;
}

export interface GetUserDataRequestAction {
  type: UserActionTypes.GET_USER_DATA_REQUEST;
}

export interface GetUserDataSuccessAction {
  type: UserActionTypes.GET_USER_DATA_SUCCESS;
  payload: IUser;
}

export interface GetUserDataFailureAction {
  type: UserActionTypes.GET_USER_DATA_FAILURE;
  payload: string;
}

export interface GetWorkspaceDataRequestAction {
  type: UserActionTypes.GET_WORKSPACE_DATA_REQUEST;
}

export interface GetWorkspaceDataSuccessAction {
  type: UserActionTypes.GET_WORKSPACE_DATA_SUCCESS;
  payload: any; // You might want to create a proper workspace type
}

export interface GetWorkspaceDataFailureAction {
  type: UserActionTypes.GET_WORKSPACE_DATA_FAILURE;
  payload: string;
}

export interface ClearUserErrorAction {
  type: UserActionTypes.CLEAR_USER_ERROR;
}

export interface ClearUserSuccessAction {
  type: UserActionTypes.CLEAR_USER_SUCCESS;
}

export interface SetUserDataAction {
  type: UserActionTypes.SET_USER_DATA;
  payload: IUser;
}

export interface ClearUserDataAction {
  type: UserActionTypes.CLEAR_USER_DATA;
}

// Union type for all user actions
export type UserAction =
  | InitializeAuthAction
  | LoginRequestAction
  | LoginSuccessAction
  | LoginFailureAction
  | LogoutRequestAction
  | LogoutSuccessAction
  | LogoutFailureAction
  | RegisterRequestAction
  | RegisterSuccessAction
  | RegisterFailureAction
  | GetUserProfileRequestAction
  | GetUserProfileSuccessAction
  | GetUserProfileFailureAction
  | UpdateUserProfileRequestAction
  | UpdateUserProfileSuccessAction
  | UpdateUserProfileFailureAction
  | GetUserDataRequestAction
  | GetUserDataSuccessAction
  | GetUserDataFailureAction
  | GetWorkspaceDataRequestAction
  | GetWorkspaceDataSuccessAction
  | GetWorkspaceDataFailureAction
  | ClearUserErrorAction
  | ClearUserSuccessAction
  | SetUserDataAction
  | ClearUserDataAction;

// User State interface
export interface UserState {
  // User data
  currentUser: IUser | null;
  workspace: IWorkspace | null; // You might want to create a proper workspace type

  // Authentication state
  isAuthenticated: boolean;
  authToken: string | null;

  // Loading states
  loading: boolean;
  loginLoading: boolean;
  registerLoading: boolean;
  profileLoading: boolean;
  updateLoading: boolean;
  userDataLoading: boolean;
  workspaceDataLoading: boolean;

  // Error states
  error: string | null;
  loginError: string | null;
  registerError: string | null;
  profileError: string | null;
  updateError: string | null;
  userDataError: string | null;
  workspaceDataError: string | null;

  // Success states
  success: string | null;
  loginSuccess: boolean;
  registerSuccess: boolean;
  updateSuccess: boolean;
  userDataSuccess: boolean;
  workspaceDataSuccess: boolean;
}
